import { useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { 
  CreditCard, Shield, Clock, MessageCircle, CheckCircle, 
  Zap, Globe, Lock, ArrowRight, ExternalLink, AlertCircle, Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

// Supported services data
const supportedServices = [
  {
    name: "ChatGPT Pro",
    logo: "https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg",
    description: "OpenAI ChatGPT Pro подписка",
    price: "2 150 ₽",
    popular: true
  },
  {
    name: "Google Gemini",
    logo: "https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg",
    description: "Google Gemini Advanced",
    price: "2 150 ₽",
    popular: true
  },
  {
    name: "Freepik Pro",
    logo: "https://customer-assets.emergentagent.com/job_c82f24ae-6a92-440c-b51b-f05e42d3e205/artifacts/bzcxpwml_Freepik-2023-Icon-Logo-Vector.svg-.png",
    description: "Premium подписка Freepik",
    price: "2 050 ₽",
    popular: false
  },
  {
    name: "Midjourney",
    logo: "https://upload.wikimedia.org/wikipedia/commons/e/e6/Midjourney_Emblem.png",
    description: "Генерация изображений",
    price: "1 350 ₽",
    popular: true
  },
  {
    name: "Claude Pro",
    logo: "https://customer-assets.emergentagent.com/job_c82f24ae-6a92-440c-b51b-f05e42d3e205/artifacts/guk02eny_Claude_AI_symbol.svg.png",
    description: "Anthropic Claude Pro",
    price: "2 150 ₽",
    popular: false
  },
  {
    name: "Emergent",
    logo: "https://customer-assets.emergentagent.com/job_c82f24ae-6a92-440c-b51b-f05e42d3e205/artifacts/tkwpsolp_2025-11-03T20-30-35.584Z-zx4HqPU.png",
    description: "AI-платформа для разработки",
    price: "2 150 ₽",
    popular: true,
    addon: "Пополнение +250 токенов — 4 850 ₽"
  },
  {
    name: "Figma Full Seat",
    logo: "https://upload.wikimedia.org/wikipedia/commons/3/33/Figma-logo.svg",
    description: "Платная команда Full Seat",
    price: "1 790 ₽",
    popular: false
  },
  {
    name: "HeyGen",
    logo: "https://customer-assets.emergentagent.com/job_5f806588-0531-4690-a34b-14f6cbf1d642/artifacts/id4vwvsj_images2.png",
    description: "AI-генерация видео с аватарами",
    price: "2 990 ₽",
    popular: true
  },
  {
    name: "ElevenLabs",
    logo: "https://customer-assets.emergentagent.com/job_5f806588-0531-4690-a34b-14f6cbf1d642/artifacts/qiv54lpf_ys8tt2ro_images.png",
    description: "AI-генерация голоса и озвучка",
    price: "2 350 ₽",
    popular: true
  }
];

const benefits = [
  {
    icon: Shield,
    title: "Безопасно",
    description: "Ваши данные защищены. Оплата производится напрямую на официальных сайтах сервисов"
  },
  {
    icon: Clock,
    title: "Быстро",
    description: "Активация подписки в течение 24 часов после оплаты"
  },
  {
    icon: CreditCard,
    title: "Удобно",
    description: "Принимаем оплату российскими картами, СБП и криптовалютой"
  },
  {
    icon: MessageCircle,
    title: "Поддержка",
    description: "Личная поддержка в Telegram на каждом этапе"
  }
];

const steps = [
  {
    num: "01",
    title: "Выберите сервис",
    description: "Определитесь, какой AI-сервис вам нужен"
  },
  {
    num: "02",
    title: "Напишите в Telegram",
    description: "Отправьте заявку с данными аккаунта"
  },
  {
    num: "03",
    title: "Оплатите",
    description: "Переведите оплату удобным способом"
  },
  {
    num: "04",
    title: "Получите доступ",
    description: "Подписка активируется в течение 24ч"
  }
];

// Telegram link
const TELEGRAM_LINK = "https://t.me/Oplata_Srvices";

export const PaymentServicePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Оплата зарубежных сервисов"
        description="Поможем оплатить зарубежные подписки из России: ChatGPT, Midjourney, Freepik, Gemini, Claude и другие. Быстро, безопасно, без переплат."
        url="/payment-service"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-[#F1EDE4] pt-28 pb-20 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2 mb-6">
              <Globe size={16} className="text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Для пользователей из России</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Оплата иностранных{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                AI-сервисов
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8 max-w-2xl mx-auto">
              Помогаем оплатить подписки на ChatGPT, Midjourney, Google Gemini и другие зарубежные нейросети, недоступные для российских карт
            </p>

            <a 
              href={TELEGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-teal-600/25 hover:-translate-y-0.5 group"
              data-testid="hero-telegram-btn"
            >
              <MessageCircle size={24} />
              Написать в Telegram
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Supported Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Поддерживаемые сервисы
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Какие сервисы можно оплатить
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Оплачиваем подписки на популярные AI-платформы и не только
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {supportedServices.map((service, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-lg hover:border-teal-300 hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${idx}`}
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                    {service.logo ? (
                      <img loading="lazy" 
                        src={service.logo} 
                        alt={service.name}
                        className="w-9 h-9 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-2xl font-bold text-gray-400">${service.name.charAt(0)}</span>`;
                        }}
                      />
                    ) : (
                      <span className="text-2xl font-bold text-gray-400">{service.name.charAt(0)}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-900 text-lg">{service.name}</h3>
                    <p className="text-sm text-gray-500 truncate">{service.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl text-teal-600 font-bold">{service.price}</span>
                      <span className="text-xs text-gray-400">/ мес</span>
                    </div>
                    {service.addon && (
                      <span className="text-xs text-purple-600">{service.addon}</span>
                    )}
                  </div>
                  <a
                    href="https://t.me/Oplata_Srvices"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="paper-primary flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    data-testid={`service-telegram-btn-${idx}`}
                  >
                    <Send size={14} />
                    Заказать
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8">
            Не нашли нужный сервис? <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Напишите нам</a> — оплатим любой!
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Преимущества
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Почему выбирают нас
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="text-center p-6"
                data-testid={`benefit-${idx}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-200">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-500 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-[#F1EDE4]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-teal-500/20">
              Как это работает
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              4 простых шага
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div 
                key={idx}
                className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                data-testid={`step-${idx}`}
              >
                <div className="text-5xl font-bold text-teal-500/20 mb-4">{step.num}</div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.description}</p>
                
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight size={20} className="text-teal-500/50" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-teal-400/80 text-sm mt-8">
            * Как правило, выполняем заявку сразу
          </p>
        </div>
      </section>

      {/* Important Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Что нужно для оплаты</h3>
                <p className="text-gray-600 mb-4">
                  Для активации подписки нам потребуются данные от вашего аккаунта на сервисе:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-700">
                    <Lock size={16} className="text-amber-600" />
                    <span><strong>Логин</strong> (email) от аккаунта</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-700">
                    <Lock size={16} className="text-amber-600" />
                    <span><strong>Пароль</strong> от аккаунта</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  Данные нужны только для входа и оплаты. После активации рекомендуем сменить пароль.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-emerald-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.2) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Готовы начать?
          </h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            Напишите нам в Telegram — ответим в течение часа и поможем оплатить нужный сервис
          </p>
          
          <a 
            href={TELEGRAM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="paper-primary inline-flex items-center gap-3 bg-white text-teal-700 font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:bg-teal-50 hover:shadow-xl hover:-translate-y-0.5 group"
            data-testid="cta-telegram-btn"
          >
            <MessageCircle size={24} />
            Написать в Telegram
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentServicePage;
